import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import {
  buildBreadcrumbJsonLd,
  buildCanonicalUrl,
  buildWebPageJsonLd,
  organizationJsonLd,
  websiteJsonLd,
} from "@/lib/seo";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Globe } from "lucide-react";
import { truncateText } from "@/lib/textUtils";
import type { Package as PackageType } from "@shared/schema";
const charDhamBanner = "/char-dham-banner.png";
import {
  MapPin,
  Calendar,
  Users,
  Star,
  ArrowRight,
  Filter,
} from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import emailjs from "@emailjs/browser";
import { buildApiUrl } from "@/lib/config";
import { analyticsEvents, trackEvent } from "@/lib/ga4";

// Import char dham banner image

export default function PackagesPage() {
  const [filter, setFilter] = useState<"all" | "domestic" | "international">(
    "all"
  );
  const [isPlanDialogOpen, setIsPlanDialogOpen] = useState(false);
  const { toast } = useToast();
  const {
    data: packages = [],
    isLoading,
    error,
  } = useQuery<PackageType[]>({
    queryKey: ["/api/packages", "all"],
    queryFn: async () => {
      const response = await fetch(buildApiUrl("api/packages"));
      if (!response.ok) {
        throw new Error("Failed to fetch packages");
      }
      return response.json();
    },
  });

  const heroPackageDetails = {
    title: "Bali Temples, Beaches & Jungle Retreat",
    summary: [
      "Ubud rice terraces, sacred temples, art markets, and jungle spa rituals.",
      "Seminyak sunsets, Uluwatu cliff temples, beach clubs, and surf-ready shores.",
      "Nusa Penida blues, snorkel stops, and East Bali gates above black-sand bays.",
      "Flexible dates • Private drivers & day trips • 24/7 concierge.",
    ],
  };

  const emailConfig = {
    serviceId: "service_4pz4dzf",
    templateId: "template_aelz1vr",
    publicKey: "uhHa5ey8ZNE5hSWsN",
    toEmails: [
      "it@travelwingsonline.in",
      "support@travelwingsonline.in",
      "hr@travelwingsonline.in",
    ],
  };

  const bookingSchema = z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Invalid email address"),
    phone: z.string().min(1, "Phone number is required"),
  });

  type BookingFormData = z.infer<typeof bookingSchema>;

  const form = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
  });

  const planMutation = useMutation({
    mutationFn: async (data: BookingFormData) => {
      const messageBody = [
        "Hello Team,",
        "",
        "A customer has shown interest in the Bali Temples, Beaches & Jungle Retreat from the packages page:",
        "",
        `Name: ${data.firstName} ${data.lastName}`,
        `Email: ${data.email}`,
        `Phone: ${data.phone}`,
        "Service Interest: Book Now",
        "",
        "Package Details:",
        `Title: ${heroPackageDetails.title}`,
        ...heroPackageDetails.summary,
        "",
        "Source: Packages Page Hero CTA",
      ].join("\n");

      const emailParams = {
        first_name: data.firstName,
        last_name: data.lastName,
        reply_to: data.email,
        user_email: data.email,
        phone: data.phone,
        service_interest: "Book Now",
        message: messageBody,
        to_email: emailConfig.toEmails.join(","),
      };

      await emailjs.send(
        emailConfig.serviceId,
        emailConfig.templateId,
        emailParams,
        { publicKey: emailConfig.publicKey }
      );

      return { status: "email_sent" };
    },
    onSuccess: () => {
      trackEvent({
        name: analyticsEvents.generateLead,
        params: {
          content_type: "lead",
          item_id: "packages_plan_hero",
          item_name: heroPackageDetails.title,
          page_type: "packages",
          page_path: "/packages",
          result: "success",
        },
      });
      toast({
        title: "Request sent successfully!",
        description: "We have received your booking request.",
      });
      form.reset();
      setIsPlanDialogOpen(false);
    },
    onError: (mutationError: Error) => {
      trackEvent({
        name: analyticsEvents.generateLead,
        params: {
          content_type: "lead",
          item_id: "packages_plan_hero",
          item_name: heroPackageDetails.title,
          page_type: "packages",
          page_path: "/packages",
          result: "error",
          message: mutationError.message,
        },
      });
      toast({
        title: "Failed to send request",
        description: mutationError.message,
        variant: "destructive",
      });
    },
  });

  const onPlanSubmit = (data: BookingFormData) => {
    planMutation.mutate(data);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Filter packages based on the filter prop
  const filteredPackages =
    packages.filter((pkg: PackageType) => {
      if (filter === "all") return true;
      if (filter === "domestic") return (pkg as any).type === "domestic";
      if (filter === "international")
        return (pkg as any).type === "international";
      return true;
    }) || [];

  useEffect(() => {
    if (!packages.length) return;
    trackEvent({
      name: analyticsEvents.search,
      params: {
        content_type: "package",
        search_term: filter,
        filters_count: filter === "all" ? 0 : 1,
        results_count: filteredPackages.length,
        page_type: "packages",
        page_path: "/packages",
      },
    });
  }, [filter, filteredPackages.length, packages.length]);

  // Early return with error handling
  if (error) {
    console.error("Package fetch error:", error);
    return (
      <div className="min-h-screen bg-white">
        <Seo
          title="Holiday Packages | Curated Domestic & International Trips"
          description="Browse curated holiday packages from Travel Wings Global with transparent pricing and expert support."
          path="/packages"
          structuredData={[
            organizationJsonLd,
            websiteJsonLd,
            buildWebPageJsonLd({
              name: "Holiday Packages | Curated Domestic & International Trips",
              description:
                "Domestic and international travel packages from Travel Wings Global with transparent pricing.",
              url: buildCanonicalUrl("/packages"),
              type: "CollectionPage",
            }),
            buildBreadcrumbJsonLd([
              { name: "Home", url: buildCanonicalUrl("/") },
              { name: "Packages", url: buildCanonicalUrl("/packages") },
            ]),
          ]}
        />
        <Navigation />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Error Loading Packages
            </h2>
            <p className="text-gray-600">{error.message}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Holiday Packages | Curated Domestic & International Trips"
        description="Discover curated domestic and international holiday packages with expert planning, transparent pricing, and support from Travel Wings Global."
        path="/packages"
        structuredData={[
          organizationJsonLd,
          websiteJsonLd,
          buildWebPageJsonLd({
            name: "Holiday Packages | Curated Domestic & International Trips",
            description:
              "Domestic and international travel packages from Travel Wings Global with transparent pricing.",
            url: buildCanonicalUrl("/packages"),
            type: "CollectionPage",
          }),
          buildBreadcrumbJsonLd([
            { name: "Home", url: buildCanonicalUrl("/") },
            { name: "Packages", url: buildCanonicalUrl("/packages") },
          ]),
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Holiday <span className="text-orange-600">Packages</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore amazing destinations with our carefully crafted travel
            packages designed for unforgettable experiences
          </p>
        </div>
      </section>

      {/* Char Dham Yatra Banner */}
      {/* <section className="py-8 md:py-16 bg-gradient-to-b from-orange-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl min-h-[500px] md:min-h-[600px]">
            <div className="absolute inset-0">
              <img 
                src={charDhamBanner} 
                alt="Char Dham Temples - Kedarnath, Badrinath, Gangotri, Yamunotri"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
            </div>

            <div className="relative z-10 flex items-center justify-center min-h-[500px] md:min-h-[600px] p-6 md:p-16">
              <div className="max-w-4xl mx-auto text-center text-white">
                <div className="mb-6">
                  <span className="text-5xl md:text-7xl">🕉️</span>
                </div>
                <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white drop-shadow-lg">
                  Char Dham Yatra
                </h2>
                <p className="text-lg md:text-2xl mb-8 text-amber-100 leading-relaxed max-w-3xl mx-auto">
                  Embark on a sacred pilgrimage to the four divine shrines of Uttarakhand - 
                  Kedarnath, Badrinath, Gangotri, and Yamunotri.
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
                  <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4 border border-white/20 hover:bg-white/20 transition-all">
                    <div className="text-2xl md:text-3xl mb-2">🏔️</div>
                    <div className="font-semibold text-sm md:text-base text-white">Kedarnath</div>
                    <div className="text-xs md:text-sm text-amber-200">Lord Shiva's Abode</div>
                  </div>
                  <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4 border border-white/20 hover:bg-white/20 transition-all">
                    <div className="text-2xl md:text-3xl mb-2">🏛️</div>
                    <div className="font-semibold text-sm md:text-base text-white">Badrinath</div>
                    <div className="text-xs md:text-sm text-amber-200">Lord Vishnu's Temple</div>
                  </div>
                  <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4 border border-white/20 hover:bg-white/20 transition-all">
                    <div className="text-2xl md:text-3xl mb-2">🌊</div>
                    <div className="font-semibold text-sm md:text-base text-white">Gangotri</div>
                    <div className="text-xs md:text-sm text-amber-200">Origin of Ganga</div>
                  </div>
                  <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4 border border-white/20 hover:bg-white/20 transition-all">
                    <div className="text-2xl md:text-3xl mb-2">⛰️</div>
                    <div className="font-semibold text-sm md:text-base text-white">Yamunotri</div>
                    <div className="text-xs md:text-sm text-amber-200">Source of Yamuna</div>
                  </div>
                </div>

                <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-8">
                  <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 md:px-6 py-2 md:py-3 rounded-full border border-white/30">
                    <Calendar className="w-4 md:w-5 h-4 md:h-5 mr-2 text-amber-200" />
                    <span className="font-medium text-sm md:text-base text-white">10-12 Days</span>
                  </div>
                  <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 md:px-6 py-2 md:py-3 rounded-full border border-white/30">
                    <Users className="w-4 md:w-5 h-4 md:h-5 mr-2 text-amber-200" />
                    <span className="font-medium text-sm md:text-base text-white">All Groups</span>
                  </div>
                  <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 md:px-6 py-2 md:py-3 rounded-full border border-white/30">
                    <Star className="w-4 md:w-5 h-4 md:h-5 mr-2 text-amber-200" />
                    <span className="font-medium text-sm md:text-base text-white">Helicopter Option</span>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-6">
                  <div className="text-center">
                    <div className="text-lg text-amber-200 mb-2">Package Starting From</div>
                    <div className="text-4xl md:text-5xl font-bold text-yellow-300 drop-shadow-lg">₹25,999</div>
                    <div className="text-sm text-amber-200">per person</div>
                  </div>
                  <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold px-8 md:px-12 py-3 md:py-4 rounded-full text-base md:text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 border-0">
                    Book Sacred Journey
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* <section className="py-6 md:py-12 bg-gradient-to-b from-amber-50 via-orange-50 to-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl min-h-[320px] md:min-h-[420px]">
            <div className="absolute inset-0">
              <img
                src="/phuket.jpg"
                alt="Turquoise Thai waters with limestone cliffs at sunset"
                loading="eager"
                fetchpriority="high"
                decoding="async"
                sizes="100vw"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-black/75 md:bg-gradient-to-r md:from-black/80 md:via-black/60 md:to-black/70"></div>
            </div>

            <div className="relative z-10 p-6 md:p-16 text-white h-full flex items-center">
              <div className="max-w-4xl mx-auto text-center w-full">
                <div className="mb-4 md:mb-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-amber-100 text-sm md:text-base tracking-wide">
                    <span className="text-2xl md:text-3xl">🌴</span>
                    <span className="uppercase">Tropical Thailand Escape</span>
                  </div>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold mb-3 md:mb-5">
                  Phuket & Krabi Twin Bay Escape
                </h2>
                <p className="text-lg md:text-2xl mb-6 md:mb-8 text-amber-100 leading-relaxed max-w-3xl mx-auto">
                  Glide across turquoise lagoons, limestone karsts, and powder
                  beaches with curated island-hopping, cliffside sunsets, and
                  beachfront cocktails under starry skies.
                </p>

                <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-6 md:mb-8">
                  <span className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-xs md:text-sm text-amber-100">
                    Beachfront resorts & pool villas
                  </span>
                  <span className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-xs md:text-sm text-amber-100">
                    Speedboat island hops & snorkel stops
                  </span>
                  <span className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-xs md:text-sm text-amber-100">
                    Sunset dinners, spas, and cliff views
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/20">
                    <div className="text-2xl md:text-3xl mb-2">🌅</div>
                    <div className="font-semibold text-sm md:text-lg">
                      Phuket
                    </div>
                    <p className="text-xs md:text-sm text-amber-100 mt-1">
                      Patong sunsets, Old Town cafes, beach clubs and nightlife
                      by the sea.
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/20">
                    <div className="text-2xl md:text-3xl mb-2">🪨</div>
                    <div className="font-semibold text-sm md:text-lg">
                      Krabi
                    </div>
                    <p className="text-xs md:text-sm text-amber-100 mt-1">
                      Railay cliffs, emerald pools, and longtail rides to hidden
                      coves.
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/20">
                    <div className="text-2xl md:text-3xl mb-2">⛵️</div>
                    <div className="font-semibold text-sm md:text-lg">
                      Phi Phi & Phang Nga
                    </div>
                    <p className="text-xs md:text-sm text-amber-100 mt-1">
                      Maya Bay lagoons, James Bond cliffs, and reef snorkels in
                      crystal coves.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
                  <div className="text-center">
                    <div className="text-sm md:text-base text-amber-200 mb-1 md:mb-2">
                      Flexible dates • Shared or private speedboats • 24/7
                      concierge
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      trackEvent({
                        name: analyticsEvents.ctaClick,
                        params: {
                          content_type: "cta",
                          item_id: "plan_phuket_krabi",
                          item_name: "Plan Phuket & Krabi Escape",
                          cta_label: "plan_trip",
                          page_type: "packages",
                          page_path: "/packages",
                        },
                      });
                      setIsPlanDialogOpen(true);
                    }}
                    className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 hover:from-amber-600 hover:via-orange-600 hover:to-rose-600 text-white font-bold px-6 md:px-8 py-3 rounded-full text-sm md:text-lg transition-all duration-300 shadow-lg"
                  >
                    Plan Your Phuket & Krabi Escape
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      <section className="py-6 md:py-12 bg-gradient-to-b from-emerald-50 via-teal-50 to-cyan-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl min-h-[320px] md:min-h-[420px]">
            <div className="absolute inset-0">
              <img
                src="/bali.jpg"
                alt="Rice terraces and tropical coastline in Bali at golden hour"
                width={1920}
                height={1080}
                loading="eager"
                fetchpriority="high"
                decoding="async"
                sizes="100vw"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-black/75 md:bg-gradient-to-r md:from-black/80 md:via-black/60 md:to-black/70"></div>
            </div>

            <div className="relative z-10 p-6 md:p-16 text-white h-full flex items-center">
              <div className="max-w-4xl mx-auto text-center w-full">
                <div className="mb-4 md:mb-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-emerald-100 text-sm md:text-base tracking-wide">
                    <span className="text-2xl md:text-3xl">🛕</span>
                    <span className="uppercase">Island of the Gods</span>
                  </div>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold mb-3 md:mb-5">
                  Bali Temples, Beaches & Jungle Retreat
                </h2>
                <p className="text-lg md:text-2xl mb-6 md:mb-8 text-emerald-100 leading-relaxed max-w-3xl mx-auto">
                  Wander tiered rice paddies, sea temples at sunset, and
                  jungle-shrouded spas—then unwind at beach clubs and infinity
                  pools overlooking the Indian Ocean.
                </p>

                <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-6 md:mb-8">
                  <span className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-xs md:text-sm text-emerald-100">
                    Private villas & boutique resorts
                  </span>
                  <span className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-xs md:text-sm text-emerald-100">
                    Temple tours & Balinese spa rituals
                  </span>
                  <span className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-xs md:text-sm text-emerald-100">
                    Surf breaks, snorkel days & volcano vistas
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/20">
                    <div className="text-2xl md:text-3xl mb-2">🌾</div>
                    <div className="font-semibold text-sm md:text-lg">
                      Ubud
                    </div>
                    <p className="text-xs md:text-sm text-emerald-100 mt-1">
                      Tegallalang terraces, sacred monkey forest, galleries, and
                      jungle cafés among the palms.
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/20">
                    <div className="text-2xl md:text-3xl mb-2">🌊</div>
                    <div className="font-semibold text-sm md:text-lg">
                      Seminyak & Uluwatu
                    </div>
                    <p className="text-xs md:text-sm text-emerald-100 mt-1">
                      Golden-hour beach clubs, Uluwatu kecak fire dances, and
                      clifftop temples above the surf.
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white/20">
                    <div className="text-2xl md:text-3xl mb-2">🏝️</div>
                    <div className="font-semibold text-sm md:text-lg">
                      Nusa & East Bali
                    </div>
                    <p className="text-xs md:text-sm text-emerald-100 mt-1">
                      Nusa Penida viewpoints, reef snorkels, and Lempuyang gates
                      over misty volcanic ridges.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
                  <div className="text-center">
                    <div className="text-sm md:text-base text-emerald-200 mb-1 md:mb-2">
                      Flexible dates • Private drivers & day trips • 24/7
                      concierge
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      trackEvent({
                        name: analyticsEvents.ctaClick,
                        params: {
                          content_type: "cta",
                          item_id: "plan_bali",
                          item_name: "Plan Bali Retreat",
                          cta_label: "plan_trip",
                          page_type: "packages",
                          page_path: "/packages",
                        },
                      });
                      setIsPlanDialogOpen(true);
                    }}
                    className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 hover:from-emerald-600 hover:via-teal-600 hover:to-cyan-600 text-white font-bold px-6 md:px-8 py-3 rounded-full text-sm md:text-lg transition-all duration-300 shadow-lg"
                  >
                    Plan Your Bali Retreat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={isPlanDialogOpen} onOpenChange={setIsPlanDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Plan Your Bali Retreat</DialogTitle>
            <DialogDescription>
              Share your details and we'll start curating your temples, beaches,
              and jungle escape.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-gray-50 p-3 rounded-md text-sm text-gray-700">
            <div className="font-semibold text-gray-900">
              {heroPackageDetails.title}
            </div>
            <ul className="list-disc pl-5 space-y-1 text-gray-600 mt-2">
              {heroPackageDetails.summary.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onPlanSubmit)}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter first name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="you@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input placeholder="+91 9876543210" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                disabled={planMutation.isPending}
              >
                {planMutation.isPending ? "Sending..." : "Send Booking Request"}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Filters Section */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center gap-6">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <span className="font-medium text-gray-700">
                Filter Packages:
              </span>
            </div>
            <div className="flex flex-col sm:flex-row bg-gray-100 rounded-lg p-1 w-full max-w-md">
              <button
                onClick={() => setFilter("all")}
                className={`px-4 py-3 rounded-md font-medium transition-colors text-sm flex-1 ${
                  filter === "all"
                    ? "bg-orange-600 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                All Packages
              </button>
              <button
                onClick={() => setFilter("domestic")}
                className={`px-4 py-3 rounded-md font-medium transition-colors text-sm flex items-center justify-center gap-2 flex-1 ${
                  filter === "domestic"
                    ? "bg-orange-600 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <MapPin className="w-4 h-4" />
                Domestic
              </button>
              <button
                onClick={() => setFilter("international")}
                className={`px-4 py-3 rounded-md font-medium transition-colors text-sm flex items-center justify-center gap-2 flex-1 ${
                  filter === "international"
                    ? "bg-orange-600 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <MapPin className="w-4 h-4" />
                International
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-800 rounded-full text-sm font-medium mb-4">
              <Globe className="w-4 h-4 mr-2" />
              Travel Packages
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Discover Your Next{" "}
              <span className="text-orange-600">Adventure</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose from our extensive collection of travel packages to
              destinations around the world
            </p>
          </div>

          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, index) => (
                <div
                  key={index}
                  className="bg-white rounded-3xl shadow-lg overflow-hidden animate-pulse"
                >
                  <div className="w-full h-64 bg-gray-300"></div>
                  <div className="p-8">
                    <div className="h-6 bg-gray-300 rounded mb-4"></div>
                    <div className="h-4 bg-gray-300 rounded mb-4"></div>
                    <div className="h-4 bg-gray-300 rounded mb-6"></div>
                    <div className="h-12 bg-gray-300 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPackages.map((pkg) => (
                <Link
                  key={pkg.id}
                  href={`/packages/${pkg.id}`}
                  aria-label={`${pkg.title}, view package details`}
                  onClick={() =>
                    trackEvent({
                      name: analyticsEvents.selectItem,
                      params: {
                        content_type: "package",
                        item_id: pkg.id,
                        item_name: pkg.title,
                        price_inr: pkg.price,
                        duration: pkg.duration,
                        destination:
                          (pkg as any).destination || (pkg as any).capacity,
                        page_type: "packages",
                        page_path: "/packages",
                      },
                    })
                  }
                >
                  <div className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 group border border-gray-100 cursor-pointer h-full flex flex-col">
                    <div className="relative overflow-hidden">
                      <img
                        src={(pkg as any).imageUrl || (pkg as any).image}
                        alt={pkg.title}
                        width={960}
                        height={256}
                        className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                        sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src =
                            "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400";
                        }}
                      />
                      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                        <span className="text-2xl font-bold text-orange-600">
                          {formatPrice(pkg.price)}
                        </span>
                      </div>
                    </div>
                    <div className="p-8 flex flex-col flex-grow">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {pkg.title}
                      </h3>
                      <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                        {truncateText(pkg.description, 18)}
                      </p>
                      <div className="flex items-center text-gray-500 mb-6 space-x-6">
                        <div className="flex items-center">
                          <Calendar className="w-5 h-5 mr-2 text-blue-500" />
                          <span className="font-medium">{pkg.duration}</span>
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-5 h-5 mr-2 text-green-500" />
                          <span className="font-medium">
                            {(pkg as any).capacity}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-8">
                        {((pkg as any).tags || [])
                          .slice(0, 3)
                          .map((tag: string, index: number) => (
                            <Badge
                              key={index}
                              className="bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1"
                            >
                              {tag}
                            </Badge>
                          ))}
                      </div>
                      <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 text-lg font-semibold rounded-xl mt-auto">
                        View Details
                      </Button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
